import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { ROUTES } from "@/config/routes";
import { isUiMockMode } from "@/config/features";
import { canDownloadZip } from "@/domain/export/canDownloadZip";
import { EXPORT_FAILED_DETAIL_HINT } from "@/domain/export/exportFailedCopy";
import { formatCreateSource } from "@/domain/export/formatExportTitle";
import { ExportStatusBadge } from "@/features/exports/components/ExportStatusBadge";
import {
  DataTableShell,
  tableClass,
  tdClass,
  thClass,
} from "@/features/exports/components/DataTableShell";
import {
  mockExportZipSize,
} from "@/fixtures/exportJobs";
import { useExportDownload, downloadErrorMessage } from "@/features/exports/hooks/useExportDownload";
import { ExportFilterCriteriaSection } from "@/features/exports/components/ExportFilterCriteriaCell";
import { formatBytes } from "@/utils/format/bytes";
import { formatDateTime } from "@/utils/format/date";
import { exportZipLinkLabel } from "@/domain/export/exportZipLinkLabel";
import { sortedExportFiles } from "@/domain/export/sortedExportFiles";
import type { ExportJobDetail, ExportJobItem } from "@/types/api";
import { cn } from "@/utils/cn";

function isItemExportSuccess(status: ExportJobItem["item_status"]): boolean {
  return status === "SUCCESS";
}

function itemStatusChipClass(s: ExportJobItem["item_status"]): string {
  if (isItemExportSuccess(s)) return "chip chip-ok";
  if (s === "FAILED") return "chip chip-fail";
  return "chip";
}

function itemStatusLabel(s: ExportJobItem["item_status"]): string {
  if (isItemExportSuccess(s)) return "Success";
  if (s === "FAILED") return "Failed";
  if (s === "SKIPPED") return "Skipped";
  if (s === "PENDING") return "Pending";
  return s;
}

function formatItemError(row: ExportJobItem): string {
  if (isItemExportSuccess(row.item_status)) return "—";
  return row.remark?.trim() || "—";
}

export function ExportDetailPanel({ job }: { job: ExportJobDetail }) {
  const download = useExportDownload();
  const items = job.items ?? [];
  const mock = isUiMockMode();
  const files = sortedExportFiles(job);
  const zipSize = mock
    ? mockExportZipSize(job)
    : job.zip_size_bytes != null
      ? formatBytes(job.zip_size_bytes)
      : null;
  const skippedCount = items.filter((r) => r.item_status === "SKIPPED").length;

  const onDownload = (fileId: number) => {
    if (mock) {
      alert("Mock mode — ZIP download is not wired to the backend.");
      return;
    }
    download.mutate(
      { jobId: job.job_id, fileId },
      { onError: (e) => alert(downloadErrorMessage(e)) },
    );
  };

  const multiVolume = files.length > 1;

  return (
    <div className="space-y-6">
      {job.status === "FAILED" ? (
        <div className="info-panel">
          <p className="text-sm text-[var(--muted)]">{EXPORT_FAILED_DETAIL_HINT}</p>
        </div>
      ) : null}
      <div className="detail-hero">
        <ExportStatusBadge status={job.status} />
        <div className="flex flex-col gap-2">
          {canDownloadZip(job.status, job.invoice_count) && files.length > 0 ? (
            multiVolume ? (
              files.map((file) => (
                <div key={file.file_id} className="flex items-center gap-3">
                  <span className="text-sm tabular-nums">
                    {exportZipLinkLabel(file.volume_index, files.length)}
                    {file.file_size_bytes != null ? (
                      <span className="ml-1.5 text-xs text-[var(--muted)]">
                        ({formatBytes(file.file_size_bytes)})
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    className="btn-main inline-flex items-center gap-1.5"
                    disabled={download.isPending}
                    onClick={() => onDownload(file.file_id)}
                  >
                    <Download size={15} />
                    Download
                  </button>
                </div>
              ))
            ) : (
              <button
                type="button"
                className="btn-main inline-flex items-center gap-1.5"
                disabled={download.isPending}
                onClick={() => onDownload(files[0].file_id)}
              >
                <Download size={15} />
                Download ZIP
              </button>
            )
          ) : null}
        </div>
        <Link to={ROUTES.invoicesPdfs} className="btn-soft">
          Export more invoice PDFs
        </Link>
      </div>

      <div className="detail-grid">
        <DetailCell
          k="Progress"
          v={`${job.invoice_success_count} / ${job.invoice_count} PDFs`}
          tabular
        />
        <DetailCell
          k="Documents exported"
          v={String(job.invoice_success_count)}
          tabular
        />
        {zipSize ? <DetailCell k="ZIP size" v={zipSize} tabular /> : null}
        <DetailCell k="Source" v={formatCreateSource(job.source)} />
        <DetailCell
          k="Finished"
          v={job.finished_at ? formatDateTime(job.finished_at) : "—"}
        />
      </div>

      <ExportFilterCriteriaSection job={job} />

      {items.length > 0 ? (
        <div className="panel overflow-hidden p-0">
          <div className="table-toolbar border-b border-[var(--border-soft)] px-4 py-2.5">
            <strong className="text-sm">Per-invoice results</strong>
            <span className="text-xs text-[var(--muted)]">
              {job.invoice_failed_count} failed · {skippedCount} skipped
            </span>
          </div>
          <DataTableShell>
            <div className="overflow-x-auto">
              <table className={tableClass}>
                <thead>
                  <tr>
                    <th className={thClass}>Invoice</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.invoice_id}>
                      <td className={`${tdClass} tabular-nums text-xs`}>
                        {row.invoice_number ?? row.invoice_id}
                      </td>
                      <td className={tdClass}>
                        <span className={cn(itemStatusChipClass(row.item_status))}>
                          {itemStatusLabel(row.item_status)}
                        </span>
                      </td>
                      <td className={`${tdClass} text-[0.82rem] text-[var(--muted)]`}>
                        {formatItemError(row)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataTableShell>
        </div>
      ) : null}
    </div>
  );
}

function DetailCell({
  k,
  v,
  tabular,
}: {
  k: string;
  v: React.ReactNode;
  tabular?: boolean;
}) {
  return (
    <div className="detail-grid-cell">
      <div className="k">{k}</div>
      <div className={cn("v", tabular && "tabular-nums")}>{v}</div>
    </div>
  );
}
